// src/game/I18n.js
// ─────────────────────────────────────────────────────────────────────────────
// Singleton i18n helper.
//
//   import { I18n } from '../game/I18n.js';
//
//   I18n.t('key')              → translated string for current locale
//   I18n.t('key', { n: 42 })  → with variable substitution ({n} → 42)
//   I18n.setLocale('en-US')    → persists to localStorage
//   I18n.getLocale()           → current locale code
//   I18n.getSuspense(element)  → translated suspense text for element type
// ─────────────────────────────────────────────────────────────────────────────

import { STRINGS } from '../data/i18n.js';

const STORAGE_KEY = 'pazurumon_locale';
const SUPPORTED   = ['pt-PT', 'en-US', 'es-ES', 'fr-FR', 'zh-CN', 'ja-JP'];
const FALLBACK    = 'pt-PT';

class I18nService {
  constructor() {
    this._locale = this._load();
  }

  _load() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return SUPPORTED.includes(saved) ? saved : FALLBACK;
    } catch {
      return FALLBACK;
    }
  }

  /** Current locale code, e.g. 'en-US' */
  getLocale() {
    return this._locale;
  }

  /** Change locale and persist. No-op for unsupported codes. */
  setLocale(code) {
    if (!SUPPORTED.includes(code)) return;
    this._locale = code;
    try { localStorage.setItem(STORAGE_KEY, code); } catch {}
  }

  /**
   * Get translated string for key.
   * Falls back to pt-PT, then to the key itself.
   * Supports {var} substitution: t('best', { n: score }) → 'Best: 9999'
   */
  t(key, vars = {}) {
    const dict = STRINGS[this._locale] || STRINGS[FALLBACK];
    let str = dict[key] ?? STRINGS[FALLBACK][key] ?? key;
    for (const [k, v] of Object.entries(vars)) {
      str = String(str).replaceAll(`{${k}}`, v);
    }
    return str;
  }

  /**
   * Returns the translated tips array for the current locale.
   */
  tips() {
    const dict = STRINGS[this._locale] || STRINGS[FALLBACK];
    return dict.tips ?? STRINGS[FALLBACK].tips ?? [];
  }

  /**
   * Returns the translated villain taunts array for the current locale.
   */
  taunts() {
    const dict = STRINGS[this._locale] || STRINGS[FALLBACK];
    return dict.taunts ?? STRINGS[FALLBACK].taunts ?? [];
  }

  /**
   * Returns the translated suspense text for a given element type.
   * element: 'fire' | 'water' | 'earth' | 'electric' | 'psychic'
   */
  getSuspense(element) {
    const dict = STRINGS[this._locale] || STRINGS[FALLBACK];
    return dict.suspense?.[element] ?? dict.suspense?.default
      ?? STRINGS[FALLBACK].suspense?.[element]
      ?? STRINGS[FALLBACK].suspense?.default
      ?? '';
  }

  /** All supported locale codes */
  getSupportedLocales() {
    return SUPPORTED;
  }
}

export const I18n = new I18nService();
